import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "../../lib/cva";
import * as React from "react";
import { flushSync } from "react-dom";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./tooltip";
import { TEXT_HOVER_INK } from "./box-classes";

export const textVariants = cva("", {
  variants: {
    size: {
      // DESIGN-TUNABLE: default is the base control idiom (16px mobile,
      // 14px ≥sm). It lives INSIDE the variant so explicit sizes replace the
      // responsive pair instead of fighting it in tw-merge.
      //
      // Each size carries its own tracking through the theme
      // (`--text-<size>--letter-spacing` in index.css): titles tighten,
      // captions open, body sits at zero. Never add `tracking-*` here.
      default: "text-sm",
      "2xs": "text-2xs", xs: "text-xs", sm: "text-sm", base: "text-base",
      lg: "text-lg", xl: "text-xl", "2xl": "text-2xl",
      "3xl": "text-3xl", "4xl": "text-4xl",
    },
    weight: {
      normal: "font-normal", medium: "font-medium",
      semibold: "font-semibold", bold: "font-bold",
    },
    variant: {
      default: "",
      muted: "text-muted-foreground",
      // The third rung of the key-text ramp (foreground, muted, faint) —
      // quiet-by-design caption text: field labels, dim section labels.
      faint: "text-faint-foreground",
      subtle: "text-muted-foreground/72",
      primary: "text-brand",
      destructive: "text-destructive-foreground",
      success: "text-success-foreground",
      warning: "text-warning-foreground",
      info: "text-info-foreground",
      // Inline link inside running text — the anchor idiom Button
      // variant="link" gives standalone actions. Pair with `as="a"`.
      link: "text-brand underline-offset-4 hover:underline",
    },
    align: { left: "text-left", center: "text-center", right: "text-right" },
    mono: { true: "font-mono" },
    // Preformatted: newlines and indentation render as written, long lines
    // wrap instead of forcing horizontal scroll — code/JSON previews.
    pre: { true: "whitespace-pre-wrap break-words" },
    italic: { true: "italic" },
    strike: { true: "line-through" },
    // Underline decoupled from `variant="link"`'s primary color, so quiet
    // actions (faint/muted text) can carry the affordance at their own ink.
    underline: {
      true: "underline underline-offset-4",
      hover: "underline-offset-4 hover:underline",
    },
    // Fixed-width digits — metrics/counters that must not jitter as they tick.
    tabular: { true: "tabular-nums" },
    // Small caps for a KIND LABEL — a kind or category word set above an
    // identity (a role over a name, a section word over a title). Caps need
    // more air than the size's own tracking gives lowercase, so this is the
    // one place a tracking utility rides Text: it names a typographic role,
    // not a size.
    caps: { true: "uppercase tracking-[0.08em]" },
    // The ink registry lives with the group scopes (`TEXT_HOVER_INK`).
    hoverInk: {
      true: TEXT_HOVER_INK.self,
      row: TEXT_HOVER_INK.row,
    },
    // "none" pins the line box to glyph height — chip labels use it so
    // ghost-wash bleeds land predictably and chip height matches the text.
    leading: {
      none: "leading-none",
      tight: "leading-tight",
      normal: "leading-normal",
    },
    // `true` is the one-line ellipsis (nowrap + text-overflow) and zeroes its
    // min-width: a flex item's `min-width: auto` is its nowrap content width,
    // which would leave the one-line variant unable to shrink at all in a row
    // — it would push the row's trailing controls out instead of ellipsizing.
    // The numbered steps are the multi-line clamp, and each one RESETS
    // `white-space` and allows an over-long word to break:
    //
    // A clamp ellipsizes at a LINE boundary, so it needs the text to wrap to
    // have a boundary at all. Under an inherited `white-space: nowrap` — which
    // every Button, Badge, Group, Tab and table cell sets — the clamp finds one
    // endless line, overflows sideways instead of downward, and the `overflow:
    // hidden` it ships with then cuts the text mid-glyph with no ellipsis. The
    // reset is what makes the prop mean what it says wherever it is used.
    //
    // `break-words` closes the same hole from the other side: a single
    // unbreakable token (an email, a username, a URL) offers no wrap
    // opportunity, so wrapping alone still leaves one endless line and the same
    // mid-glyph cut. Letting it break at the box edge gives the clamp its
    // boundary. It is `overflow-wrap: break-word`, not `anywhere`, on purpose —
    // `anywhere` also drops min-content width to a single character, which
    // would silently let every auto-sized column and flex item in the app
    // collapse further than it does today.
    truncate: {
      true: "min-w-0 truncate",
      1: "line-clamp-1 whitespace-normal break-words",
      2: "line-clamp-2 whitespace-normal break-words",
      3: "line-clamp-3 whitespace-normal break-words",
      4: "line-clamp-4 whitespace-normal break-words",
      5: "line-clamp-5 whitespace-normal break-words",
      6: "line-clamp-6 whitespace-normal break-words",
    },
    // A bright band sweeps across dimmed ink, the "Generating…" treatment.
    // The ink is painted by the background, so it wins over `variant` and
    // `color`. Reduced motion (the OS or the viewer's preference) rests on
    // full ink with no sweep.
    shimmer: {
      true: [
        "[background:linear-gradient(100deg,transparent_35%,var(--foreground)_50%,transparent_65%)_0_0/250%_100%_no-repeat,color-mix(in_oklab,var(--foreground)_80%,transparent)]",
        "bg-clip-text text-transparent motion-safe:animate-text-shimmer",
        "motion-reduce:[background:none] motion-reduce:text-foreground",
      ].join(" "),
    },
  },
  defaultVariants: { size: "default", variant: "default" },
});

export type TextElement =
  | "span" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  | "label" | "code" | "blockquote" | "a"
  // Preformatted text (stack traces, raw payloads): the UA's
  // `white-space: pre` survives preflight, so author line breaks hold and a
  // scrolling container owns the overflow. Pair with `mono`.
  | "pre";

interface TextOwnProps extends VariantProps<typeof textVariants> {
  /**
   * A palette or semantic color NAME. Emits `data-color` and `text-c`, which
   * `tokens.css` binds to the hue in both modes. Wins over `variant`'s color.
   */
  color?: Color;
  /**
   * Hovering the text shows its FULL content in a tooltip — but only while the
   * text is actually clipped, measured live at hover time, so a value that
   * happens to fit stays tooltip-free and a tooltip never restates something
   * already on screen. On a coarse pointer, where hover never fires, a held
   * press reads it instead (the `Tooltip` press-reveal), and the text gives
   * up its long-press selection callout for it.
   *
   * Pair it with `truncate`. Truncating is what keeps a long value from pushing
   * its trailing neighbours (inline actions, a chevron) out of reach; this is
   * how the reader gets the part that truncating took away. Text that carries a
   * richer hover surface already — an entity chip's preview card — does not
   * take this: the card is the disclosure, and two hover layers on one word
   * fight each other.
   */
  overflowTooltip?: boolean;
  /**
   * This text is CONTENT — read and copied — inside a press surface that
   * turned selection off (a `Box group`, a table row, a kanban card): a
   * comment body in a card that also carries hover actions, a description
   * under a row that opens on press. Restores selection and the long-press
   * callout for this element. A control's label never takes it.
   */
  selectable?: boolean;
  /**
   * Present for assistive technology only: the text keeps its place in the
   * accessibility tree and paints nothing. For a name a visual already
   * carries — an icon-only crumb's label, a dialog's title when its header
   * is a breadcrumb.
   */
  visuallyHidden?: boolean;
}

// Generic over the rendered element — `as` IS the type parameter, so
// `as="a"` carries href/target/rel and `as="label"` carries htmlFor.
export type TextProps<T extends TextElement = "span"> = Omittable<
  TextOwnProps & { as?: T }
> &
  Omit<useRender.ComponentProps<T>, keyof TextOwnProps | "as">;

export function Text<T extends TextElement = "span">({
  as, render, className, size, weight, variant, align, mono, pre, italic,
  strike, underline, tabular, caps, hoverInk, leading, truncate, shimmer, color,
  overflowTooltip, selectable, visuallyHidden,
  ...props
}: TextProps<T>): React.ReactElement {
  const defaultProps = {
    className: cn(
      textVariants({
        size, weight, variant, align, mono, pre, italic, strike, underline,
        tabular, caps, hoverInk, leading, truncate, shimmer,
      }),
      color && "text-c",
      selectable && "select-text [-webkit-touch-callout:default]",
      visuallyHidden && "sr-only",
      className,
    ),
    "data-color": color,
    "data-slot": "text",
  };
  const element = useRender({
    defaultTagName: as ?? "span",
    props: mergeProps<"span">(
      defaultProps,
      props as useRender.ComponentProps<"span">,
    ),
    render,
  });

  // The tooltip's state lives one component down, not behind a branch here:
  // Text renders in the thousands in a virtualized table, and hooks cannot be
  // conditional, so machinery declared here would cost every one of those
  // instances whether or not it asked for a tooltip.
  if (!overflowTooltip) return element;
  return <TextOverflowTooltip>{element}</TextOverflowTooltip>;
}

/**
 * Wraps a rendered Text in a tooltip that is enabled only while the text is
 * actually clipped. The text element IS the trigger — nothing wraps it in the
 * DOM — so the layout the caller composed is untouched.
 *
 * Enabled, not merely refusing to open: Base UI lets an ENCLOSING tooltip
 * trigger open only while the pointer is not over an enabled nested trigger.
 * A value that fits must therefore read as no trigger at all, or the label
 * around it (a card property row naming its field) would never open over
 * the text — only over the padding beside it. A clipped value stays the
 * enabled nested trigger and wins the hover: the full text is the disclosure.
 */
function TextOverflowTooltip({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement {
  // The trigger part is typed for the `<button>` it renders by default, but
  // here it renders the caller's text element — so the ref holds what is really
  // in it, and a callback ref accepts the wider type without a cast.
  const textRef = React.useRef<HTMLElement | null>(null);
  const setTextRef = React.useCallback((node: HTMLElement | null) => {
    textRef.current = node;
  }, []);
  const [open, setOpen] = React.useState(false);
  const [clipped, setClipped] = React.useState(false);
  const [label, setLabel] = React.useState("");

  // Measured when the pointer arrives, never at mount: the content, the clamp
  // and the width available to it all change under the element, and a resize
  // observer here would run for every truncating label on the screen to
  // answer a question only the hovered one is asking. `scrollWidth` catches
  // the one-line ellipsis (which never wraps), `scrollHeight` the clamp
  // (which does, and hides the overflowing lines).
  // flushSync, because the answer has to be in the DOM before the `mouseover`
  // that follows this `pointerover` in the same input dispatch: that is where
  // an enclosing trigger looks for an enabled nested trigger, and where this
  // trigger's own hover hook must already be attached to open on a clipped
  // value. Batched normally, the flip would land after both. A touch contact
  // fires the same `pointerenter` ahead of its `pointerdown`, so the
  // press-reveal's innermost-enabled-trigger check reads the fresh answer too.
  const handlePointerEnter = React.useCallback(() => {
    const node = textRef.current;
    const isClipped =
      node !== null &&
      (node.scrollWidth > node.clientWidth ||
        node.scrollHeight > node.clientHeight);
    flushSync(() => setClipped(isClipped));
  }, []);

  const handleOpenChange = React.useCallback((next: boolean) => {
    if (next) setLabel(textRef.current?.textContent ?? "");
    setOpen(next);
  }, []);

  return (
    <Tooltip disabled={!clipped} onOpenChange={handleOpenChange} open={open}>
      <TooltipTrigger
        // A held press is the touch route into the tooltip, and on iOS a
        // held press on text is otherwise the selection callout — the two
        // cannot share the gesture, so on a coarse pointer the readout wins.
        className="pointer-coarse:select-none pointer-coarse:[-webkit-touch-callout:none]"
        onPointerEnter={handlePointerEnter}
        ref={setTextRef}
        render={children}
      />
      <TooltipPopup>{label}</TooltipPopup>
    </Tooltip>
  );
}
